import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Box, Button, IconButton, Tooltip, Typography, useTheme } from '@mui/material';
import { PrimaryGrid } from '@ui/components/common/Grid';
import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import ViewAttendeesDialog from '../ViewAttendeesDialog';
import VehicleRowActionsMenu from '../VehicleRowActionsMenu';
import NewVehicleDialog from '../NewVehicleDialog';
import ConfirmationDialog from '@ui/components/common/Dialog/ConfirmationDialog';
import { useAppDispatch, useAppSelector } from '@application/store/useStore';
import { getVehiclesByPickupLocationIdAsync, deletePickupLocationVehicleAsync } from '@application/features/pickupLocationVehicle/pickupLocationVehicleThunk';
import { getPickupLocationByIdAsync } from '@application/features/pickupLocation/pickupLocationThunk';
import { getFamilyFestPickupLocationByIdAsync } from '@application/features/familyFest/familyFestPickupLocationThunk';
import { FamilyFestGetVehiclesPaginatedAsync, FamilyFestDeleteVehicleAsync } from '@application/features/familyFest/familyFestPickupLocationVehicleThunk';
import HNumberStepperField from '@ui/components/common/TextField/HNumberStepperField';
import { CollaboratorAccessLevel } from '@application/enums/collaborator-access-level';
import { updateVehicleAttendeeCountAsync } from '@application/features/attendeePickupAssignment/attendeePickupAssignmentThunk';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { updateFamilyFestVehicleAttendeeCountAsync } from '@application/features/familyFest/familyFestPickupAssignmentThunk';
const VehicleTab = ({ filterOpen, onFilterOpenChange, searchValue, locationId, isFamilyFest }) => {
    const theme = useTheme();
    const { t } = useTranslation();
    const [viewAttendeesOpen, setViewAttendeesOpen] = useState(false);
    const [viewCommentsOpen, setViewCommentsOpen] = useState(false);
    const [editVehicleOpen, setEditVehicleOpen] = useState(false);
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [vehicleToDelete, setVehicleToDelete] = useState(null);
    const [menuAnchorEl, setMenuAnchorEl] = useState(null);
    const [menuRow, setMenuRow] = useState(null);
    const [selectedVehicleForAttendees, setSelectedVehicleForAttendees] = useState(null);
    const [vehicleToEdit, setVehicleToEdit] = useState(null);
    const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
    const [sortModel, setSortModel] = useState([]);
    const [filters, setFilters] = useState({});
    const { paginatedVehicles: standardPaginatedVehicles, vehicle: standardVehicle } = useAppSelector((state) => state.pickupLocationVehicle);
    const { familyFestVehiclesPaginated, familyFestVehicle } = useAppSelector((state) => state.familyFestPickupLocationVehicle);
    const paginatedVehicles = isFamilyFest
        ? familyFestVehiclesPaginated
        : standardPaginatedVehicles;
    const vehicle = isFamilyFest ? familyFestVehicle : standardVehicle;
    const { pickupLocation } = useAppSelector((state) => state.pickupLocation);
    const dispatch = useAppDispatch();
    const currentUser = useAppSelector((state) => state.auth.currentUser);
    const selectedCompanyIds = useAppSelector((state) => state.company.selectedCompanyId);
    const isTransportManager = Array.isArray(currentUser?.role) && currentUser?.role.includes('TransportManager');
    const isExternalTransportManager = isTransportManager &&
        Number(currentUser?.CollaboratorAccessLevel) === CollaboratorAccessLevel.External;
    const [assignedCounts, setAssignedCounts] = useState({});
    const [draftCounts, setDraftCounts] = useState({});
    const draftCountsRef = useRef({});
    draftCountsRef.current = draftCounts;
    useEffect(() => {
        if (locationId) {
            const sort = sortModel[0];
            if (isFamilyFest) {
                dispatch(FamilyFestGetVehiclesPaginatedAsync({
                    pickupLocationId: String(locationId),
                    pageNumber: paginationModel.page + 1,
                    pageSize: paginationModel.pageSize,
                    searchString: searchValue || undefined,
                    vehicleNumber: filters.vehicleNumber,
                    carrierName: filters.carrierName,
                    driverNumber: filters.driverNumber,
                    capacity: filters.capacity,
                    wheelchairSpots: filters.wheelchairSpots,
                    sortBy: sort?.field,
                    sortOrder: sort?.sort ?? 'desc'
                    // mapping counts if needed for filters
                }));
            }
            else {
                dispatch(getVehiclesByPickupLocationIdAsync({
                    pickupLocationId: String(locationId),
                    pageNumber: paginationModel.page + 1,
                    pageSize: paginationModel.pageSize,
                    searchString: searchValue || undefined,
                    vehicleNumber: filters.vehicleNumber,
                    wheelchairSpots: filters.wheelchairSpots,
                    adults: filters.adults,
                    kids: filters.kids,
                    coordinatorName: filters.coordinatorName
                }));
            }
        }
    }, [dispatch, locationId, paginationModel.page, paginationModel.pageSize, sortModel, searchValue, filters, selectedCompanyIds]);
    // Refresh list when a vehicle is created or updated
    useEffect(() => {
        if (vehicle && locationId) {
            const sort = sortModel[0];
            if (isFamilyFest) {
                dispatch(FamilyFestGetVehiclesPaginatedAsync({
                    pickupLocationId: locationId,
                    pageNumber: paginationModel.page + 1,
                    pageSize: paginationModel.pageSize,
                    searchString: searchValue || undefined,
                    vehicleNumber: filters.vehicleNumber,
                    carrierName: filters.carrierName,
                    driverNumber: filters.driverNumber,
                    capacity: filters.capacity,
                    wheelchairSpots: filters.wheelchairSpots,
                    sortBy: sort?.field,
                    sortOrder: sort?.sort ?? 'desc'
                }));
            }
            else {
                dispatch(getVehiclesByPickupLocationIdAsync({
                    pickupLocationId: locationId,
                    pageNumber: paginationModel.page + 1,
                    pageSize: paginationModel.pageSize,
                    searchString: searchValue || undefined,
                    vehicleNumber: filters.vehicleNumber,
                    wheelchairSpots: filters.wheelchairSpots,
                    adults: filters.adults,
                    kids: filters.kids,
                    coordinatorName: filters.coordinatorName
                }));
            }
        }
    }, [
        vehicle,
        dispatch,
        locationId,
        paginationModel.page,
        paginationModel.pageSize,
        sortModel,
        searchValue,
        filters
    ]);
    useEffect(() => {
        if (!paginatedVehicles?.items)
            return;
        setAssignedCounts((prev) => {
            const next = { ...prev };
            paginatedVehicles.items.forEach((v) => {
                if (draftCountsRef.current[v.id] === undefined) {
                    next[v.id] = v.assignedCount ?? 0;
                }
            });
            return next;
        });
    }, [paginatedVehicles]);
    const handleOpenRowMenu = (event, row) => {
        event.stopPropagation();
        setMenuAnchorEl(event.currentTarget);
        setMenuRow(row);
    };
    const handleCloseRowMenu = () => {
        setMenuAnchorEl(null);
        setMenuRow(null);
    };
    const handleViewComments = () => {
        handleCloseRowMenu();
        setViewCommentsOpen(true);
    };
    const handleViewAttendees = (row) => {
        setSelectedVehicleForAttendees(row);
        setViewAttendeesOpen(true);
    };
    const handleDelete = (row) => {
        handleCloseRowMenu();
        const vehicleId = row.id;
        setVehicleToDelete({
            id: vehicleId,
            vehicleNumber: row.vehicleNumber
        });
        setDeleteConfirmOpen(true);
    };
    const handleConfirmDelete = async () => {
        if (!vehicleToDelete)
            return;
        try {
            if (isFamilyFest) {
                await dispatch(FamilyFestDeleteVehicleAsync(vehicleToDelete.id)).unwrap();
            }
            else {
                await dispatch(deletePickupLocationVehicleAsync(vehicleToDelete.id)).unwrap();
            }
            toast.success(t('events.eventDetails.transportTab.vehicleDeletedSuccessfully'));
            // Refresh the list
            if (locationId) {
                if (isFamilyFest) {
                    dispatch(FamilyFestGetVehiclesPaginatedAsync({
                        pickupLocationId: locationId,
                        pageNumber: paginationModel.page + 1,
                        pageSize: paginationModel.pageSize,
                        searchString: searchValue || undefined
                    }));
                }
                else {
                    dispatch(getVehiclesByPickupLocationIdAsync({
                        pickupLocationId: locationId,
                        pageNumber: paginationModel.page + 1,
                        pageSize: paginationModel.pageSize,
                        searchString: searchValue || undefined
                    }));
                }
                // Refresh pickup location details
                if (isFamilyFest) {
                    dispatch(getFamilyFestPickupLocationByIdAsync(String(locationId)));
                }
                else {
                    dispatch(getPickupLocationByIdAsync(String(locationId)));
                }
            }
            setDeleteConfirmOpen(false);
            setVehicleToDelete(null);
        }
        catch (error) {
            const errorMessage = error?.message || t('events.eventDetails.transportTab.vehicleDeleteFailed');
            toast.error(errorMessage);
        }
    };
    const columns = [
        {
            field: 'vehicleNumber',
            type: 'string',
            headerName: t('events.eventDetails.transportTab.vehicleNumber'),
            flex: 0.8,
            minWidth: 120,
            renderCell: (params) => {
                return (_jsxs("div", { style: { display: 'flex', flexDirection: 'column', lineHeight: 1.2 }, children: [_jsx("span", { children: params.row.vehicleNumber || '—' }), isFamilyFest && (_jsx("span", { style: { fontSize: 12, color: '#888' }, children: params.row.carrierName || '—' }))] }));
            }
        },
        ...(isFamilyFest
            ? [
                {
                    field: 'carrierName',
                    type: 'string',
                    headerName: t('events.eventDetails.transportTab.carrierName') || 'Carrier Name',
                    flex: 0.8,
                    minWidth: 120
                },
                {
                    field: 'driverNumber',
                    type: 'string',
                    headerName: t('events.eventDetails.transportTab.driverNumber') || 'Driver Number',
                    flex: 0.8,
                    minWidth: 120,
                    renderCell: (params) => {
                        return params.row.driverNumber || '—';
                    }
                }
            ]
            : []),
        {
            field: 'capacity',
            headerName: t('events.eventDetails.transportTab.seats'),
            // Without adults/kids columns, flex:1 leaves a large empty stretch before Wheelchair.
            flex: isFamilyFest ? 0.8 : 1,
            minWidth: isFamilyFest ? 120 : 140,
            renderCell: (params) => {
                const vehicle = params.row;
                if (vehicle.totalCapacity == null || vehicle.assignedCount == null) {
                    return '—';
                }
                const capacityText = `${vehicle.assignedCount}/${vehicle.totalCapacity}`;
                const adults = Number(vehicle.adultsCount ?? 0);
                const kids = Number(vehicle.kidsCount ?? 0);
                const wheelchair = Number(vehicle.wheelchairCount ?? 0);
                const tooltipContent = isFamilyFest ? (_jsx(Box, { component: 'span', sx: { display: 'block' }, children: _jsxs(Box, { component: 'ul', sx: { m: 0, pl: 2, '& li': { py: 0.25 } }, children: [_jsxs("li", { style: { ...theme.typography.caption }, children: [t('events.eventDetails.transportTab.totalSeats'), ": ", vehicle.totalCapacity] }), _jsxs("li", { style: { ...theme.typography.caption }, children: [t('events.eventDetails.transportTab.assigned'), ": ", vehicle.assignedCount] }), _jsxs("li", { style: { ...theme.typography.caption }, children: [t('events.eventDetails.transportTab.wheelChair'), ": ", wheelchair] })] }) })) : (_jsxs(Box, { component: 'span', sx: { display: 'block' }, children: [_jsx(Typography, { variant: 'caption', sx: { fontWeight: 400, mb: 0.5 }, children: t('events.eventDetails.transportTab.seatAssignments') }), _jsxs(Box, { component: 'ul', sx: { m: 0, pl: 2, '& li': { py: 0.25 } }, children: [_jsxs("li", { style: { ...theme.typography.caption }, children: [adults, " ", t('events.eventDetails.transportTab.adults').toLowerCase()] }), _jsxs("li", { style: { ...theme.typography.caption }, children: [kids, " ", t('events.eventDetails.transportTab.kids').toLowerCase()] }), _jsxs("li", { style: { ...theme.typography.caption }, children: [wheelchair, " ", t('events.eventDetails.transportTab.wheelChair').toLowerCase()] })] })] }));
                return (_jsx(Tooltip, { title: tooltipContent, placement: 'top', slotProps: {
                        popper: {
                            sx: {
                                '& .MuiTooltip-tooltip': {
                                    bgcolor: 'grey.900',
                                    color: 'common.white',
                                    borderRadius: 2,
                                    px: 1.5,
                                    py: 1.25,
                                    typography: 'caption'
                                }
                            }
                        }
                    }, children: _jsx(Box, { component: 'span', sx: { cursor: 'default' }, children: capacityText }) }));
            }
        },
        {
            field: 'wheelchairSpots',
            type: 'number',
            headerName: t('events.eventDetails.transportTab.wheelChair'),
            flex: 0.8,
            minWidth: 110,
            renderCell: (params) => {
                const wheelchairSpots = isFamilyFest
                    ? params.row.wheelchairCount
                    : params.row.wheelchairSpots;
                return wheelchairSpots != null ? String(wheelchairSpots) : '—';
            }
        },
        ...(!isFamilyFest
            ? [
                {
                    field: 'adults',
                    type: 'number',
                    headerName: t('events.eventDetails.transportTab.adults'),
                    flex: 0.8,
                    minWidth: 90,
                    renderCell: (params) => {
                        const adultsVal = params.row.adults;
                        return adultsVal != null ? String(adultsVal) : '—';
                    }
                },
                {
                    field: 'kids',
                    type: 'number',
                    headerName: t('events.eventDetails.transportTab.kids'),
                    flex: 0.8,
                    minWidth: 90,
                    renderCell: (params) => {
                        const kidsVal = params.row.kids;
                        return kidsVal != null ? String(kidsVal) : '—';
                    }
                }
            ]
            : []),
        ...(!isExternalTransportManager
            ? [
                {
                    field: 'coordinatorName',
                    headerName: t('events.eventDetails.transportTab.coordinator'),
                    flex: 1.2,
                    minWidth: 160,
                    renderCell: (params) => {
                        return params.row.coordinatorName || '—';
                    }
                }
            ]
            : []),
        {
            field: 'actions',
            headerName: isExternalTransportManager
                ? t('events.eventDetails.transportTab.assignedAttendees')
                : '',
            flex: 1.2,
            minWidth: 180,
            sortable: false,
            filterable: false,
            renderCell: (params) => {
                const vehicle = params.row;
                const rowRecord = {
                    id: vehicle.id,
                    vehicleNumber: vehicle.vehicleNumber,
                    vehicleId: vehicle.id
                };
                const savedCount = assignedCounts[vehicle.id] ?? vehicle.assignedCount ?? 0;
                const draftValue = draftCounts[vehicle.id];
                const displayValue = draftValue !== undefined ? draftValue : savedCount;
                const isDirty = draftValue !== undefined;
                const unassignedCount = pickupLocation?.unassignedAttendeeCount;
                const maxAssignable = unassignedCount !== undefined
                    ? Math.min(vehicle.totalCapacity, savedCount + unassignedCount)
                    : vehicle.totalCapacity;
                const handleAssignedCountChange = (value) => {
                    const nextValue = Math.min(maxAssignable, Math.max(0, value));
                    setDraftCounts((prev) => ({ ...prev, [vehicle.id]: nextValue }));
                };
                const handleConfirmCount = async () => {
                    if (draftValue === undefined)
                        return;
                    const change = Math.abs(draftValue - savedCount);
                    const isAssign = draftValue > savedCount;
                    if (change === 0) {
                        setDraftCounts((prev) => {
                            const next = { ...prev };
                            delete next[vehicle.id];
                            return next;
                        });
                        return;
                    }
                    try {
                        let response;
                        if (isFamilyFest) {
                            response = await dispatch(updateFamilyFestVehicleAttendeeCountAsync({
                                vehicleId: vehicle.id,
                                isAssign,
                                attendeeCount: change
                            })).unwrap();
                        }
                        else {
                            response = await dispatch(updateVehicleAttendeeCountAsync({
                                vehicleId: vehicle.id,
                                isAssign,
                                attendeeCount: change
                            })).unwrap();
                        }
                        if (!response.isSuccess) {
                            setDraftCounts((prev) => {
                                const next = { ...prev };
                                delete next[vehicle.id];
                                return next;
                            });
                            toast.error(response.message ||
                                (isAssign
                                    ? t('events.eventDetails.transportTab.attendeeAssignFailed')
                                    : t('events.eventDetails.transportTab.attendeeUnassignFailed')));
                            return;
                        }
                        setAssignedCounts((prev) => ({ ...prev, [vehicle.id]: draftValue }));
                        setDraftCounts((prev) => {
                            const next = { ...prev };
                            delete next[vehicle.id];
                            return next;
                        });
                        toast.success(response.message ||
                            (isAssign
                                ? t('events.eventDetails.transportTab.attendeeAssignedSuccessfully')
                                : t('events.eventDetails.transportTab.attendeeUnassignedSuccessfully')));
                        if (isFamilyFest) {
                            dispatch(FamilyFestGetVehiclesPaginatedAsync({
                                pickupLocationId: locationId,
                                pageNumber: paginationModel.page + 1,
                                pageSize: paginationModel.pageSize,
                                searchString: searchValue || undefined
                            }));
                        }
                        else {
                            dispatch(getVehiclesByPickupLocationIdAsync({
                                pickupLocationId: locationId,
                                pageNumber: paginationModel.page + 1,
                                pageSize: paginationModel.pageSize,
                                searchString: searchValue || undefined
                            }));
                        }
                        if (isFamilyFest) {
                            dispatch(getFamilyFestPickupLocationByIdAsync(String(locationId)));
                        }
                        else {
                            dispatch(getPickupLocationByIdAsync(String(locationId)));
                        }
                    }
                    catch (error) {
                        setDraftCounts((prev) => {
                            const next = { ...prev };
                            delete next[vehicle.id];
                            return next;
                        });
                        toast.error(error?.message ||
                            (isAssign
                                ? t('events.eventDetails.transportTab.attendeeAssignFailed')
                                : t('events.eventDetails.transportTab.attendeeUnassignFailed')));
                    }
                };
                const handleCancelCount = () => {
                    setDraftCounts((prev) => {
                        const next = { ...prev };
                        delete next[vehicle.id];
                        return next;
                    });
                };
                return (_jsxs(Box, { display: 'flex', alignItems: 'center', gap: 1, children: [!isExternalTransportManager ? (_jsx(Button, { variant: 'outlined', color: 'primary', size: 'small', disabled: pickupLocation?.isClosed, onClick: (e) => {
                                e.stopPropagation();
                                handleViewAttendees({ id: vehicle.id, vehicleNumber: vehicle.vehicleNumber });
                            }, children: t('events.eventDetails.transportTab.viewAttendees') })) : (_jsxs(Box, { display: 'flex', alignItems: 'center', gap: 0.5, children: [_jsx(HNumberStepperField, { value: displayValue, min: 0, max: maxAssignable, step: 1, onChange: handleAssignedCountChange, disabled: pickupLocation?.isClosed, textFieldProps: { size: 'small' } }), isDirty && !pickupLocation?.isClosed && (_jsxs(_Fragment, { children: [_jsx(IconButton, { size: 'small', color: 'success', onMouseDown: (e) => e.preventDefault(), onClick: (e) => {
                                                e.stopPropagation();
                                                handleConfirmCount();
                                            }, disabled: pickupLocation?.isClosed, sx: { p: 0.25 }, children: _jsx(CheckIcon, { fontSize: 'small' }) }), _jsx(IconButton, { size: 'small', color: 'error', onMouseDown: (e) => e.preventDefault(), onClick: (e) => {
                                                e.stopPropagation();
                                                handleCancelCount();
                                            }, disabled: pickupLocation?.isClosed, sx: { p: 0.25 }, children: _jsx(CloseIcon, { fontSize: 'small' }) })] }))] })), !pickupLocation?.isClosed && (_jsx(IconButton, { size: 'small', color: 'inherit', onClick: (e) => handleOpenRowMenu(e, rowRecord), children: _jsx(MoreHorizIcon, { fontSize: 'small', sx: { color: theme.palette.text.secondary } }) }))] }));
            }
        }
    ];
    return (_jsxs(Box, { children: [_jsx(PrimaryGrid, { columns: columns, rows: paginatedVehicles?.items || [], rowHeight: 80, showHeaderControls: false, showNewHeaderControls: true, filterOpen: filterOpen, onFilterOpenChange: onFilterOpenChange, onApplyFilters: (newFilters) => {
                    setFilters(newFilters);
                    setPaginationModel((prev) => ({ ...prev, page: 0 }));
                }, paginationModel: paginationModel, onPaginationModelChange: setPaginationModel, paginationMode: 'server', sortingMode: 'server', sortModel: sortModel, onSortModelChange: (model) => {
                    setSortModel(model);
                    setPaginationModel((prev) => ({ ...prev, page: 0 }));
                }, rowCount: paginatedVehicles?.totalCount || 0, pageSizeOptions: [10, 25, 50, 100], hideFooter: false, ...(isFamilyFest && { columnVisibilityModel: { carrierName: false } }), sx: {
                    height: 'calc(100dvh - 464px)'
                } }), _jsx(ViewAttendeesDialog, { open: viewAttendeesOpen, onClose: () => {
                    setViewAttendeesOpen(false);
                    setSelectedVehicleForAttendees(null);
                }, vehicleNumber: selectedVehicleForAttendees?.vehicleNumber, vehicleId: selectedVehicleForAttendees?.id, locationId: locationId, isFamilyFest: isFamilyFest }), _jsx(VehicleRowActionsMenu, { anchorEl: menuAnchorEl, open: Boolean(menuAnchorEl), onClose: handleCloseRowMenu, vehicleRow: menuRow, commentCount: 5, isdisabled: pickupLocation?.isClosed, onViewComments: handleViewComments, onEdit: (row) => {
                    handleCloseRowMenu();
                    const vehicleData = paginatedVehicles?.items?.find((v) => v.id === row.id);
                    if (vehicleData) {
                        setVehicleToEdit(vehicleData);
                        setEditVehicleOpen(true);
                    }
                    else {
                        console.warn('Vehicle data not found for editing', row);
                    }
                }, onDelete: handleDelete }), _jsx(NewVehicleDialog, { open: editVehicleOpen, onClose: () => {
                    setEditVehicleOpen(false);
                    setVehicleToEdit(null);
                }, locationId: locationId, vehicleToEdit: vehicleToEdit, isFamilyFest: isFamilyFest, onSuccess: () => {
                    // Refresh pickup location details after vehicle creation/update
                    if (locationId) {
                        if (isFamilyFest) {
                            dispatch(getFamilyFestPickupLocationByIdAsync(String(locationId)));
                        }
                        else {
                            dispatch(getPickupLocationByIdAsync(String(locationId)));
                        }
                    }
                } }, vehicleToEdit?.id || 'new'), _jsx(ConfirmationDialog, { open: deleteConfirmOpen, onClose: () => {
                    setDeleteConfirmOpen(false);
                    setVehicleToDelete(null);
                }, title: t('events.eventDetails.transportTab.deleteVehicle'), actions: _jsxs(_Fragment, { children: [_jsx(Button, { fullWidth: true, variant: 'outlined', size: 'large', color: 'inherit', onClick: () => {
                                setDeleteConfirmOpen(false);
                                setVehicleToDelete(null);
                            }, children: t('actions.cancel') }), _jsx(Button, { fullWidth: true, variant: 'contained', size: 'large', color: 'error', onClick: handleConfirmDelete, children: t('actions.deleteNow') })] }), children: _jsx(Box, { p: 2, children: _jsx(Typography, { variant: 'body1', color: 'text.primary', children: t('events.eventDetails.transportTab.deleteVehicleConfirmation', {
                            vehicleNumber: vehicleToDelete?.vehicleNumber || ''
                        }) }) }) })] }));
};
export default VehicleTab;
